import React, { useState, useEffect, useRef } from "react";
import apiClient from "../../../../../api/apiClient";
import Loading from "../../../../../components/Loading/Loading";

// ── Helpers ───────────────────────────────────────────────────────────────────

// Only treat as full HTML document (e.g. campaign emails) — not rich-text replies
function looksLikeHTML(str) {
    if (!str) return false;
    const s = str.trim();
    return /<!doctype\s+html/i.test(s) || /<html[\s>]/i.test(s);
}

const EmailIframe = ({ html }) => {
    const ref = useRef(null);

    const resize = () => {
        const iframe = ref.current;
        if (!iframe) return;
        try {
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            const h = doc?.documentElement?.scrollHeight || doc?.body?.scrollHeight || 0;
            if (h) iframe.style.height = `${h}px`;
        } catch { /* sandboxed cross-origin guard */ }
    };
    
    return (
        <iframe
            ref={ref}
            className="crm-email-iframe"
            srcDoc={html}
            onLoad={resize}
            title="Email content"
            sandbox="allow-same-origin"
            scrolling="no"
        />
    );
};

function getInitials(from) {
    if (!from) return "?";
    const nameMatch = from.match(/^"?([^"<]+?)"?\s*</);
    const name = nameMatch ? nameMatch[1].trim() : from;
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
}

function parseFrom(from) {
    if (!from) return { name: "", email: "" };
    const m = from.match(/^"?([^"<]+?)"?\s*<([^>]+)>/);
    if (m) return { name: m[1].trim(), email: m[2].trim() };
    return { name: from, email: from };
}

const PALETTE = ["#6366f1", "#8b5cf6", "#0ea5e9", "#10b981", "#f59e0b", "#ec4899", "#6b7280"];
function avatarColor(str) {
    let h = 0;
    for (let i = 0; i < (str || "").length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
    return PALETTE[Math.abs(h) % PALETTE.length];
}

function formatDate(dt) {
    if (!dt) return "";
    return new Date(dt).toLocaleString([], {
        month: "short", day: "numeric", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
}

// ── Icons ─────────────────────────────────────────────────────────────────────

const BoldIcon      = () => <strong style={{ fontFamily: "Georgia, serif", fontSize: 13, lineHeight: 1 }}>B</strong>;
const ItalicIcon    = () => <em     style={{ fontFamily: "Georgia, serif", fontSize: 13, lineHeight: 1 }}>I</em>;
const UnderIcon     = () => <span   style={{ textDecoration: "underline", fontSize: 13, lineHeight: 1 }}>U</span>;
const StrikeIcon    = () => <span   style={{ textDecoration: "line-through", fontSize: 13, lineHeight: 1 }}>S</span>;

const BulletIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <line x1="9" y1="6" x2="20" y2="6"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="18" x2="20" y2="18"/>
        <circle cx="4" cy="6" r="1.5" fill="currentColor" stroke="none"/>
        <circle cx="4" cy="12" r="1.5" fill="currentColor" stroke="none"/>
        <circle cx="4" cy="18" r="1.5" fill="currentColor" stroke="none"/>
    </svg>
);

const NumberedIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/>
        <path d="M4 6h1v4M4 10h2" strokeWidth="1.8"/>
        <path d="M4 14h2l-2 4h2" strokeWidth="1.8"/>
    </svg>
);

const QuoteIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/>
        <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/>
    </svg>
);

const LinkIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
    </svg>
);

const PaperclipIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
    </svg>
);

const ImageAttachIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21 15 16 10 5 21"/>
    </svg>
);

const SendIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13"/>
        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
);

const FileChipIcon = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
        <polyline points="13 2 13 9 20 9"/>
    </svg>
);

// ── Sub-components ────────────────────────────────────────────────────────────

const ComposerChip = ({ file, onRemove }) => (
    <div className="crm-composer-chip">
        <FileChipIcon />
        <span className="crm-composer-chip__name">{file.name}</span>
        <span className="crm-composer-chip__size">{(file.size / 1024).toFixed(0)}KB</span>
        <button
            type="button"
            className="crm-composer-chip__remove"
            onClick={() => onRemove(file.name, file.size)}
            aria-label={`Remove ${file.name}`}
        >×</button>
    </div>
);

const FmtBtn = ({ onMouseDown, title, children }) => (
    <button type="button" className="crm-composer-fmt-btn" onMouseDown={onMouseDown} title={title}>
        {children}
    </button>
);

const ThreadMessage = ({ msg }) => {
    const { name: senderName, email: senderEmail } = parseFrom(msg.fromAddress);
    const isInbound  = msg.direction === "inbound";
    const isCampaign = msg.source === "campaign";

    const dirClass = isCampaign
        ? "thread-msg--campaign"
        : isInbound
            ? "thread-msg--inbound"
            : "thread-msg--outbound";

    const displayName = senderName || senderEmail;
    const showEmail   = senderName && senderEmail !== senderName;

    return (
        <div className={`thread-msg ${dirClass}`}>
            {/* Avatar column with spine */}
            <div className="thread-msg__avatar-col">
                <div className="thread-msg__avatar" style={{ background: avatarColor(msg.fromAddress || "") }}>
                    {getInitials(msg.fromAddress)}
                </div>
                <div className="thread-msg__spine" />
            </div>

            {/* Content */}
            <div className="thread-msg__content">
                <div className="thread-msg__meta">
                    <span className="thread-msg__sender">{displayName}</span>
                    {showEmail && (
                        <span className="thread-msg__addr">{senderEmail}</span>
                    )}
                    {isCampaign && <span className="thread-msg__tag thread-msg__tag--campaign">Campaign</span>}
                    {isInbound  && <span className="thread-msg__tag thread-msg__tag--inbound">Inbound</span>}
                    <span className="thread-msg__time">{formatDate(msg.sentAt || msg.createdAt)}</span>
                </div>
                {msg.toAddress && (
                    <div className="thread-msg__to">
                        <span className="thread-msg__to-label">to</span>
                        <span className="thread-msg__to-addr">{msg.toAddress}</span>
                    </div>
                )}
                <div className="thread-msg__body">
                    {looksLikeHTML(msg.body)
                        ? <EmailIframe html={msg.body} />
                        : msg.body
                            ? <div dangerouslySetInnerHTML={{ __html: msg.body }} />
                            : <span className="thread-msg__empty">(no content)</span>
                    }
                </div>
            </div>
        </div>
    );
};

// ── Main Component ────────────────────────────────────────────────────────────

const ConversationThread = ({ conversationId, admins, onBack }) => {
    const [conversation, setConversation]   = useState(null);
    const [messages, setMessages]           = useState([]);
    const [loading, setLoading]             = useState(true);
    const [error, setError]                 = useState("");
    const [sending, setSending]             = useState(false);
    const [sendError, setSendError]         = useState("");
    const [attachments, setAttachments]     = useState([]);
    const [statusUpdating, setStatusUpdating] = useState(false);

    const bottomRef     = useRef(null);
    const editorRef     = useRef(null);
    const fileInputRef  = useRef(null);
    const imageInputRef = useRef(null);

    const fetchThread = async () => {
        try {
            const res = await apiClient.get(`/admin/crm/conversations/${conversationId}`);
            setConversation(res.data.conversation);
            // Sort by createdAt (server-set) — sentAt from inbound email headers is unreliable
            const sorted = (res.data.messages || []).slice().sort((a, b) => {
                const tA = new Date(a.createdAt).getTime();
                const tB = new Date(b.createdAt).getTime();
                return tA !== tB ? tA - tB : (a.id > b.id ? 1 : -1);
            });
            setMessages(sorted);
            setError("");
        } catch {
            setError("Failed to load conversation");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        fetchThread();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationId]);

    useEffect(() => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    // ── Formatting ────────────────────────────────────────────────────────────

    const fmt = (e, cmd, value = null) => {
        e.preventDefault();
        editorRef.current?.focus();
        document.execCommand(cmd, false, value);
    };

    const handleLinkInsert = (e) => {
        e.preventDefault();
        const url = window.prompt("Enter URL:", "https://");
        if (url) {
            editorRef.current?.focus();
            document.execCommand("createLink", false, url);
        }
    };

    // ── Attachments ───────────────────────────────────────────────────────────

    const handleFiles = (fileList) => {
        const incoming = Array.from(fileList).filter(
            f => !attachments.find(a => a.name === f.name && a.size === f.size)
        );
        setAttachments(prev => [...prev, ...incoming]);
    };

    const removeAttachment = (name, size) => {
        setAttachments(prev => prev.filter(f => !(f.name === name && f.size === size)));
    };

    // ── Send ──────────────────────────────────────────────────────────────────

    const handleSendReply = async () => {
        const html        = editorRef.current?.innerHTML || "";
        const textContent = editorRef.current?.textContent?.trim() || "";
        if (!textContent && attachments.length === 0) return;

        setSending(true);
        setSendError("");
        try {
            const fd = new FormData();
            fd.append("body", html || " ");
            attachments.forEach(f => fd.append("files", f));
            await apiClient.post(`/admin/crm/conversations/${conversationId}/reply`, fd);
            if (editorRef.current) editorRef.current.innerHTML = "";
            setAttachments([]);
            await fetchThread();
        } catch (e) {
            setSendError(e?.response?.data?.error || "Failed to send reply");
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
            e.preventDefault();
            handleSendReply();
        }
    };

    // ── Status ────────────────────────────────────────────────────────────────

    const handleToggleStatus = async () => {
        if (!conversation) return;
        const newStatus = conversation.status === "open" ? "closed" : "open";
        setStatusUpdating(true);
        try {
            await apiClient.patch(`/admin/crm/conversations/${conversationId}`, { status: newStatus });
            setConversation(prev => ({ ...prev, status: newStatus }));
        } catch { /* ignore */ }
        setStatusUpdating(false);
    };

    if (loading) return <div className="admin-loading-wrapper"><Loading /></div>;
    if (error) return (
        <div style={{ padding: "var(--space-5)" }}>
            <button className="crm-thread-back-btn" onClick={onBack}>← Inbox</button>
            <div className="admin-form-error" style={{ marginTop: "var(--space-3)" }}>{error}</div>
        </div>
    );

    const assignedAdmin = admins?.find(a => a.userId === conversation.assignedTo);
    const isOpen = conversation.status === "open";

    return (
        <div className="crm-thread-root">

            {/* ── Thread header ── */}
            <div className="crm-thread-header">
                <div className="crm-thread-header__left">
                    <button className="crm-thread-back-btn" onClick={onBack}>← Inbox</button>
                    <h2 className="crm-thread-subject">{conversation.subject || "(no subject)"}</h2>
                    <div className="crm-thread-meta">
                        <span className="crm-thread-meta__lead">
                            {conversation.firstName} {conversation.lastName}
                        </span>
                        <span className="crm-thread-meta__sep">·</span>
                        <a href={`mailto:${conversation.leadEmail}`} className="crm-thread-meta__email">
                            {conversation.leadEmail}
                        </a>
                        {conversation.campaignName && (
                            <>
                                <span className="crm-thread-meta__sep">·</span>
                                <span className="crm-thread-meta__campaign">{conversation.campaignName}</span>
                            </>
                        )}
                        {assignedAdmin && (
                            <>
                                <span className="crm-thread-meta__sep">·</span>
                                <span className="crm-thread-meta__assigned">
                                    {assignedAdmin.firstName} {assignedAdmin.lastName}
                                </span>
                            </>
                        )}
                    </div>
                </div>
                <div className="crm-thread-header__right">
                    <span className={`crm-status-badge crm-conv-status--${conversation.status}`}>
                        {conversation.status}
                    </span>
                    <button
                        className={`admin-btn${isOpen ? "" : " admin-btn--primary"}`}
                        onClick={handleToggleStatus}
                        disabled={statusUpdating}
                    >
                        {isOpen ? "Close" : "Reopen"}
                    </button>
                </div>
            </div>

            {/* ── Message feed ── */}
            <div className="crm-thread-feed">
                {messages.length === 0 ? (
                    <div className="crm-thread-empty">No messages yet.</div>
                ) : (
                    messages.map(msg => <ThreadMessage key={msg.id} msg={msg} />)
                )}
                <div ref={bottomRef} />
            </div>

            {/* ── Reply composer ── */}
            {isOpen && (
                <div className="crm-conv-composer">
                    <div className="crm-composer-toolbar">
                        <FmtBtn onMouseDown={(e) => fmt(e, "bold")}                    title="Bold (Ctrl+B)">       <BoldIcon />       </FmtBtn>
                        <FmtBtn onMouseDown={(e) => fmt(e, "italic")}                  title="Italic (Ctrl+I)">     <ItalicIcon />     </FmtBtn>
                        <FmtBtn onMouseDown={(e) => fmt(e, "underline")}               title="Underline (Ctrl+U)">  <UnderIcon />      </FmtBtn>
                        <FmtBtn onMouseDown={(e) => fmt(e, "strikeThrough")}           title="Strikethrough">       <StrikeIcon />     </FmtBtn>
                        <span className="crm-composer-fmt-sep" />
                        <FmtBtn onMouseDown={(e) => fmt(e, "insertUnorderedList")}     title="Bullet list">         <BulletIcon />     </FmtBtn>
                        <FmtBtn onMouseDown={(e) => fmt(e, "insertOrderedList")}       title="Numbered list">       <NumberedIcon />   </FmtBtn>
                        <FmtBtn onMouseDown={(e) => fmt(e, "formatBlock", "blockquote")} title="Blockquote">        <QuoteIcon />      </FmtBtn>
                        <span className="crm-composer-fmt-sep" />
                        <FmtBtn onMouseDown={handleLinkInsert}                         title="Insert link">         <LinkIcon />       </FmtBtn>
                        <span className="crm-composer-fmt-sep" />
                        <FmtBtn onMouseDown={(e) => { e.preventDefault(); fileInputRef.current?.click(); }}  title="Attach file">  <PaperclipIcon />    </FmtBtn>
                        <FmtBtn onMouseDown={(e) => { e.preventDefault(); imageInputRef.current?.click(); }} title="Attach image"> <ImageAttachIcon />  </FmtBtn>
                    </div>

                    {attachments.length > 0 && (
                        <div className="crm-composer-chips">
                            {attachments.map(f => (
                                <ComposerChip
                                    key={f.name + f.size}
                                    file={f}
                                    onRemove={removeAttachment}
                                />
                            ))}
                        </div>
                    )}

                    <div
                        ref={editorRef}
                        className="crm-composer-body"
                        contentEditable
                        suppressContentEditableWarning
                        data-placeholder="Write a reply… (Ctrl+Enter to send)"
                        onKeyDown={handleKeyDown}
                    />

                    {sendError && (
                        <div className="admin-form-error" style={{ margin: "0 var(--space-5) var(--space-2)" }}>
                            {sendError}
                        </div>
                    )}

                    <div className="crm-composer-footer">
                        <div style={{ flex: 1 }} />
                        <button
                            className="admin-btn admin-btn--primary crm-composer-send-btn"
                            onClick={handleSendReply}
                            disabled={sending}
                        >
                            {sending ? "Sending…" : <><SendIcon /> Send Reply</>}
                        </button>
                    </div>

                    <input ref={fileInputRef}  type="file" multiple style={{ display: "none" }}
                        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
                    <input ref={imageInputRef} type="file" accept="image/*" multiple style={{ display: "none" }}
                        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
                </div>
            )}
        </div>
    );
};

export default ConversationThread;
